
/* spellchecker: disable */

import {auxiliaries, Camera, gl_matrix_extensions, mat4, ray_math, vec2, vec3} from 'webgl-operate';
import { CameraModifier } from 'webgl-operate';

/* spellchecker: enable */

const v2 = gl_matrix_extensions.v2;
const v3 = gl_matrix_extensions.v3;
const sign = gl_matrix_extensions.sign;

const DEG2RAD = auxiliaries.DEG2RAD;
const assert = auxiliaries.assert;

export interface CoordsAccess {
    (x: GLint, y: GLint, zInNDC?: number,
        viewProjectionInverse?: mat4): vec3 | undefined;
}

/* tslint:disable:max-classes-per-file */

/**
 * Support class to represent a vertex in different reference spaces.
 */
class Vertex {
    screen: vec2 | undefined = vec2.create();
    world: vec3 | undefined = vec3.create();
}

export abstract class AbstractNavigationModifier extends CameraModifier {

    protected static readonly HALF_SQUARE_LENGTH = 2.0;
    protected static readonly SCALE_FACTOR = 0.004;
    protected static readonly SCALE_STEP_FACTOR = 16.0;
    protected static readonly ROTATE_FACTOR = 0.002;

    /** @todo make the maxDistanceToSquare configurable */
    protected static readonly MAX_DISTANCE_TO_SQUARE = 4.0;
    protected static readonly MIN_NEAR_PLANE_FACTOR = 4.0;

    protected _camera: Camera;

    protected _coordsAccess: CoordsAccess;

    /**
     * Tuple for initial (initiate) two screen space and world space position pairs.
     */
    protected initialPoints = [new Vertex(), new Vertex()];
    /**
     * Tuple for current (update) two screen space and world space position pairs.
     */
    protected currentPoints = [new Vertex(), new Vertex()];

    protected initialEye = vec3.create();
    protected initialCenter = vec3.create();
    protected initialViewProjectionInverse = mat4.create();

    protected _override: boolean;

    protected _maxNegativeTranslate: vec3 | undefined;
    protected _maxPositiveTranslate: vec3 | undefined;

    // protected _minYAboveSceneGeometry: number;
    protected _minScale: number | undefined;
    protected _maxScale: number | undefined;

    protected _xAxisScreenSpace = vec3.create();

    protected _maxAngles: [number | undefined, number] = [+Math.PI * 0.6666 - Math.PI, (90.0 - 30.0) * DEG2RAD - Math.PI];
    protected _minAngles: [number | undefined, number] = [-Math.PI * 0.6666 - Math.PI, -Math.PI + 1.0 * DEG2RAD];

    /**
     * Reference plane in hesse normal form. This is used for coordinate retrieval after initiation.
     * Value sequence: location, unit normal, distance from origin to plane.
     */
    protected referencePlane: [vec3, vec3, number] = [vec3.create(), vec3.create(), 0.0];

    get valid(): boolean {
        return undefined !== this.initialPoints[0].world;
    }

    set camera(camera: Camera) {
        this._camera = camera;
    }

    needStart(): boolean {
        return this._maxAngles === undefined || this._minAngles === undefined;
    }

    set coordsAccess(coordsAccess: CoordsAccess) {
        this._coordsAccess = coordsAccess;
    }

    protected static rayY0SquareIntersection(ray0: vec3, ray1: vec3): vec3 | undefined {
        const intersection = ray_math.rayPlaneIntersection(ray0, ray1);
        if (undefined === intersection) {
            return undefined;
        }

        const i2 = vec2.fromValues(intersection[0], intersection[2]);
        const withinSquare = ray_math.isPointWithinSquare(i2, AbstractNavigationModifier.HALF_SQUARE_LENGTH);

        return withinSquare ? intersection : undefined;
    }

    protected assert_valid(): void {
        // assert(this.valid, `${this} expected to be valid`);
    }

    protected invalidate(): void {
        this.initialPoints[0].world = undefined;
        this.currentPoints[0].world = undefined;
        this.initialPoints[1].world = undefined;
        this.currentPoints[1].world = undefined;
    }

    /**
     * Compute TRANSLATION CONSTRAINTS: the camera's center is restricted to y = 0 and to be within the
     * square. The maximum allowed translation distance for x and z-axis is given by the distance of the
     * camera's center to the square edges for each of the axes.
     * @param override - If true, minimal translate constraints are used. Preferred constraints are
     * applied otherwise.
     */
    protected initiateTranslateConstraints(override: boolean): void {
        if (override) {
            this._maxNegativeTranslate = undefined;
            this._maxPositiveTranslate = undefined;
            return;
        }

        const lowerLeft = vec3.fromValues(-1.0, 0.0, -1.0);
        const upperRight = vec3.fromValues(+1.0, 0.0, +1.0);

        this._maxNegativeTranslate = vec3.sub(v3(), lowerLeft, this.initialCenter);
        this._maxPositiveTranslate = vec3.sub(v3(), upperRight, this.initialCenter);
    }

    protected abstract initiateScaleConstraints(override: boolean): void;

    /**
     * Compute ROTATE CONSTRAINTS: (1) y-axis rotation is limited to a small range, always keeping the
     * front of the square in 'forward facing'. This decreases chance of unintended orientation and
     * miscommunication of the depicted geometry. (2) the x-axis rotation stop near the bottom (cannot
     * move eye below y = 0), furthermore an additional angular offset is used to make square
     * translation feasible (full front view does not expose square are to pan on). x-axis rotation also
     * stops right before reaching the up-vector.
     * @param override - If true, minimal rotate constraints are used. Preferred constraints are applied
     * otherwise.
     */
    protected initiateRotateConstraints(override: boolean): void {
        const centerToEyeRay = vec3.normalize(v3(),
            vec3.sub(v3(), this.initialEye, this.initialCenter));
        this._xAxisScreenSpace = vec3.cross(v3(), [0.0, 1.0, 0.0], centerToEyeRay);

        const yAngle = Math.acos(vec3.dot(centerToEyeRay, [0.0, 1.0, 0.0]));

        /** @todo refine horizontal rotation constraints */

        this._maxAngles = [undefined, (90.0 - 0.01) * DEG2RAD - yAngle];
        this._minAngles = [undefined, -yAngle + 0.01 * DEG2RAD];

        if (override) {
            return;
        }

        const centerToEyeRay2 = vec2.normalize(v2(), [centerToEyeRay[0], centerToEyeRay[2]]);
        let xAngle = Math.asin(vec2.dot(centerToEyeRay2, [1.0, 0.0]));
        if (centerToEyeRay2[1] < 0.0) {
            xAngle = sign(xAngle) * Math.PI - xAngle;
        }

        /** @todo move magic numbers to interaction configuration or static member... */
        this._maxAngles = [+Math.PI * 0.6666 - xAngle, (90.0 - 30.0) * DEG2RAD - yAngle];
        this._minAngles = [-Math.PI * 0.6666 - xAngle, -yAngle + 1.0 * DEG2RAD];
    }

    /**
     * Derive 3D coordinates in world (intersections with scene geometry or square), save required
     * camera properties as initial/reference for applying transformations later (@see apply), and
     * create the reference plane for intersection computations in subsequent updates (@see update). The
     * two positions might not be order independent: The first position (pos0) is used as primary
     * reference, the second position (pos1) as secondary reference. For a valid transformation at least
     * a primary position is required.
     * @param pos0 - 2D position in view/canvas space for initial contact point retrieval (primary)
     * @param pos1 - 2D position in view/canvas space for initial contact point retrieval (secondary)
     * @param constraints - Flags for initialization of various constraints [translate, scale, rotate].
     * This is intended to reduce the amount of computations when targeting specific transformations.
     * @param override - Whether or not the constraints should restrictive or minimal (override).
     */
    initiate(pos0: vec2, pos1?: vec2
        , constraints: boolean[] = [false, false, false], override: boolean = false): boolean {
        this._override = override;

        const viewProjectionInverse = this._camera.viewProjectionInverse;
        if (!viewProjectionInverse) {
            return false;
        }
        mat4.copy(this.initialViewProjectionInverse, viewProjectionInverse);
        vec3.copy(this.initialEye, this._camera.eye);
        vec3.copy(this.initialCenter, this._camera.center);

        vec2.copy(this.initialPoints[0].screen!, pos0);
        this.initialPoints[0].world = this.coordsAt(pos0, false);
        if (undefined === this.initialPoints[0].world) {
            this.invalidate();
            return false;
        }
        this.initialPoints[1].screen = undefined === pos1 ? undefined : vec2.copy(v2(), pos1);
        this.initialPoints[1].world = this.coordsAt(pos1!, false);

        // Derive reference plane for upcoming coordinate retrieval.
        this.referencePlane[0] = this.initialPoints[0].world!;
        this.referencePlane[1] = vec3.fromValues(0.0, 1.0, 0.0);
        this.referencePlane[2] = vec3.length(this.initialPoints[0].world!);

        /**
         * @todo implement the plane computation given two points
         */

        /* The camera's center is expected to be constrained to the ground plane y = 0. */
        assert(this.initialCenter[1] === 0.0,
            `camera center is expected to be on y = 0 (ground plane)`);

        // this._minYAboveSceneGeometry = NavigationModifier.MIN_NEAR_PLANE_FACTOR * this._camera.near;

        if (constraints[0]) {
            this.initiateTranslateConstraints(override);
        }
        if (constraints[1]) {
            this.initiateScaleConstraints(override);
        }
        if (constraints[2]) {
            this.initiateRotateConstraints(override);
        }

        return true;
    }

    /**
     *
     * @param pos0 - 2D position in view/canvas space for subsequent contact point retrieval
     * @param pos1 - 2D position in view/canvas space for subsequent contact point retrieval
     *
     * @return True if the update resulted in valid current positions.
     */
    update_positions(pos0: vec2, pos1?: vec2): boolean {
        this.assert_valid();

        /* Subsequent intersections always hit the reference plane (except when override). */
        this.currentPoints[0].world = this.coordsAt(pos0);
        if (this._override && undefined === this.currentPoints[0].world) {
            this.invalidate();
            return false;
        }
        vec2.copy(this.currentPoints[0].screen!, pos0);

        if (undefined === pos1) {
            return true;
        }

        this.currentPoints[1].world = this.coordsAt(pos1);
        if (this._override && undefined === this.currentPoints[1].world) {
            this.invalidate();
            return false;
        }
        this.currentPoints[1].screen = undefined === pos1 ? undefined : vec2.copy(v2(), pos1);

        return true;
    }

    //TODO translate does not have to be abstract. Consider moving implementation to abstractNavigationModifier.ts.
    /**
     * Creates a transform for translating the camera eye and center by the difference between the
     * initial intersection point with the scene and subsequent intersections with the initial reference
     * plane. This results in a constrained panning with the user holding the initial contact point
     * within the scene in hand.
     */
    abstract translate(): void ;

    /**
     * Scales the view frustum of the camera. Does not change the position of the camera in space.
     * @see AbstractNavigationModifier.scale
     */
    abstract scale(step?: number): void;

    /**
     * Rotate the camera at the center.
     */
    abstract rotate(): void;

    /**
     * Computes a world space position based on view space position. Note that if no geometry is hit,
     * i.e., the far plane is hit, a position on a square y = 0 in [-2,-2] to [+2,+2] is returned. If
     * this is not hit as well, undefined is returned.
     * @param pos - Position in view space (canvas space).
     * @returns The world space position represented at the view/canvas space position pos. If pos is
     * undefined, undefined is returned.
     */
    coordsAt(pos: vec2, reference: boolean = true): vec3 | undefined {
        if (pos === undefined) {
            return undefined;
        }

        const viewProjectionInverse = reference ?
            this.initialViewProjectionInverse : this._camera.viewProjectionInverse;

        if (reference) {
            assert(undefined !== this.referencePlane[0] && undefined !== this.referencePlane[1]
                , `valid reference plane expected for reference coordinate retrieval`);

            const ln = this._coordsAccess(pos[0], pos[1], 0.0, viewProjectionInverse!);
            const lf = this._coordsAccess(pos[0], pos[1], 1.0, viewProjectionInverse!);

            return ray_math.rayPlaneIntersection(ln!, lf!,
                this.referencePlane[0], this.referencePlane[1]);
        }

        // Intersection with scene geometry (depth buffer look-up):
        const intersection = this._coordsAccess(pos[0], pos[1], undefined, viewProjectionInverse!);
        if (undefined !== intersection) {
            return intersection;
        }

        // Intersection with square y = 0 in [-2,-2] to [+2,+2]:
        const ln = this._coordsAccess(pos[0], pos[1], 0.0, viewProjectionInverse!);
        const lf = this._coordsAccess(pos[0], pos[1], 1.0, viewProjectionInverse!);

        return AbstractNavigationModifier.rayY0SquareIntersection(ln!, lf!);
    }

    update(): void { }
}
